import type { FightSfx } from '../fight/types';
import type { SfxName } from '../game/types';
import type { AudioEngine } from './engine';

/** Every sound effect in the game, synthesized on demand. */
export class Sfx {
  private sizzleGain: GainNode | null = null;
  private rainGain: GainNode | null = null;
  private lastPlayed = new Map<SfxName, number>();

  constructor(private readonly engine: AudioEngine) {}

  /** Continuous ambience: grill sizzle scales with how much is cooking; rain during storms. */
  ambience(grillItems: number, raining: boolean, active: boolean): void {
    const e = this.engine;
    if (!e.ctx) return;
    if (!this.sizzleGain) {
      this.sizzleGain = this.loopNoise('highpass', 2600, 0.7);
      this.rainGain = this.loopNoise('lowpass', 1400, 0.5);
    }
    const t = e.ctx.currentTime;
    const sizzle = active ? Math.min(0.11, grillItems * 0.028) : 0;
    this.sizzleGain.gain.setTargetAtTime(sizzle, t, 0.2);
    this.rainGain?.gain.setTargetAtTime(active && raining ? 0.16 : 0, t, 0.6);
  }

  private loopNoise(type: BiquadFilterType, freq: number, q: number): GainNode {
    const e = this.engine;
    const ctx = e.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = e.noise();
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = 0;
    src.connect(f).connect(g).connect(e.sfxBus);
    src.start();
    return g;
  }

  play(name: SfxName): void {
    const e = this.engine;
    if (!e.ctx) return;
    // Don't machine-gun identical sounds within a few ms of each other.
    const now = e.now;
    const last = this.lastPlayed.get(name) ?? -1;
    if (now - last < 0.035) return;
    this.lastPlayed.set(name, now);
    const t = now;

    switch (name) {
      case 'drop':
        e.hiss({ dur: 0.45, gain: 0.22, filter: 'highpass', freq: 2800, attack: 0.01 });
        e.tone({ type: 'sine', freq: 130, endFreq: 60, dur: 0.09, gain: 0.25 });
        break;
      case 'topping':
        e.hiss({ dur: 0.14, gain: 0.12, filter: 'bandpass', freq: 4200, q: 1.5 });
        e.tone({ type: 'square', freq: 620, endFreq: 940, dur: 0.05, gain: 0.05 });
        break;
      case 'scrape':
        e.hiss({ dur: 0.16, gain: 0.12, filter: 'bandpass', freq: 2600, endFreq: 1100, q: 2 });
        break;
      case 'plate':
        e.tone({ type: 'triangle', freq: 1850, dur: 0.07, gain: 0.12 });
        e.tone({ type: 'triangle', freq: 2640, at: t + 0.03, dur: 0.06, gain: 0.07 });
        break;
      case 'orderUp':
      case 'bell':
        e.tone({ type: 'sine', freq: 1568, dur: 0.9, gain: 0.2 });
        e.tone({ type: 'triangle', freq: 3136, dur: 0.4, gain: 0.04 });
        if (name === 'bell') {
          e.tone({ type: 'sine', freq: 1568, at: t + 0.18, dur: 0.9, gain: 0.18 });
          e.tone({ type: 'sine', freq: 1568, at: t + 0.36, dur: 1.1, gain: 0.16 });
        }
        break;
      case 'ding':
        e.tone({ type: 'square', freq: 1047, dur: 0.1, gain: 0.07 });
        e.tone({ type: 'square', freq: 1319, at: t + 0.11, dur: 0.14, gain: 0.07 });
        break;
      case 'pour':
        e.hiss({ dur: 0.45, gain: 0.14, filter: 'lowpass', freq: 900 });
        e.tone({ type: 'sine', freq: 220, endFreq: 150, dur: 0.3, gain: 0.06 });
        break;
      case 'coffee':
        e.hiss({ dur: 0.6, gain: 0.12, filter: 'bandpass', freq: 1100, endFreq: 2600, q: 3 });
        break;
      case 'cash':
        e.tone({ type: 'square', freq: 2093, dur: 0.05, gain: 0.05 });
        e.tone({ type: 'square', freq: 2637, at: t + 0.06, dur: 0.14, gain: 0.05 });
        break;
      case 'perfect':
        [1047, 1319, 1568, 2093].forEach((f, i) =>
          e.tone({ type: 'square', freq: f, at: t + i * 0.055, dur: 0.12, gain: 0.06 }),
        );
        e.tone({ type: 'sine', freq: 1568, dur: 0.7, gain: 0.12 });
        break;
      case 'error':
        e.tone({ type: 'square', freq: 150, dur: 0.1, gain: 0.08 });
        e.tone({ type: 'square', freq: 112, at: t + 0.09, dur: 0.14, gain: 0.08 });
        break;
      case 'door':
        [2637, 3136, 2794].forEach((f, i) =>
          e.tone({ type: 'triangle', freq: f, at: t + i * 0.07, dur: 0.25, gain: 0.05 }),
        );
        break;
      case 'walkout':
        [311, 294, 277, 247].forEach((f, i) =>
          e.tone({
            type: 'sawtooth',
            freq: f,
            endFreq: i === 3 ? f * 0.92 : undefined,
            at: t + i * 0.28,
            dur: i === 3 ? 0.7 : 0.26,
            gain: 0.06,
          }),
        );
        break;
      case 'whoosh':
        e.hiss({
          dur: 1.1,
          gain: 0.3,
          filter: 'bandpass',
          freq: 300,
          endFreq: 3200,
          q: 2,
          attack: 0.3,
        });
        e.tone({ type: 'sine', freq: 180, endFreq: 520, dur: 1, gain: 0.08, attack: 0.2 });
        break;
      case 'catch':
        e.tone({ type: 'square', freq: 190, endFreq: 80, dur: 0.1, gain: 0.3 });
        e.hiss({ dur: 0.1, gain: 0.25, filter: 'highpass', freq: 2000 });
        [523, 659, 784, 1047].forEach((f) =>
          e.tone({ type: 'square', freq: f, at: t + 0.08, dur: 0.5, gain: 0.05 }),
        );
        break;
      case 'crash':
        e.hiss({ dur: 0.7, gain: 0.5, filter: 'lowpass', freq: 2400 });
        e.tone({ type: 'sine', freq: 95, endFreq: 38, dur: 0.35, gain: 0.45 });
        for (let i = 0; i < 6; i++)
          e.tone({
            type: 'triangle',
            freq: 2500 + Math.random() * 3000,
            at: t + 0.05 + i * 0.05,
            dur: 0.12,
            gain: 0.04,
          });
        break;
      case 'fightBell':
        e.tone({ type: 'sine', freq: 880, dur: 0.8, gain: 0.18 });
        e.tone({ type: 'sine', freq: 1760, dur: 0.4, gain: 0.05 });
        e.tone({ type: 'sine', freq: 880, at: t + 0.22, dur: 0.9, gain: 0.18 });
        break;
      case 'thunder':
        e.hiss({ dur: 2.6, gain: 0.45, filter: 'lowpass', freq: 380, endFreq: 90, attack: 0.04 });
        e.tone({ type: 'sine', freq: 55, endFreq: 35, dur: 2, gain: 0.25, attack: 0.1 });
        break;
      case 'powerDown':
        e.tone({ type: 'sawtooth', freq: 420, endFreq: 45, dur: 0.9, gain: 0.12 });
        break;
      case 'powerUp':
        e.tone({ type: 'sawtooth', freq: 55, endFreq: 440, dur: 0.6, gain: 0.08 });
        e.tone({ type: 'square', freq: 1200, at: t + 0.6, dur: 0.04, gain: 0.05 });
        break;
      case 'alarm':
        for (let i = 0; i < 3; i++)
          e.tone({ type: 'square', freq: 3150, at: t + i * 0.32, dur: 0.18, gain: 0.06 });
        break;
      case 'glitch':
        for (let i = 0; i < 10; i++)
          e.tone({
            type: 'square',
            freq: 80 + Math.random() * 1800,
            at: t + i * 0.035,
            dur: 0.03,
            gain: 0.07,
          });
        e.tone({ type: 'sawtooth', freq: 55, endFreq: 40, dur: 1.4, gain: 0.12 });
        break;
      case 'toss':
        e.hiss({ dur: 0.18, gain: 0.14, filter: 'bandpass', freq: 900 });
        e.tone({ type: 'sine', freq: 110, endFreq: 70, dur: 0.12, gain: 0.2, at: t + 0.1 });
        break;
      case 'crowdOoh':
        this.crowd(t);
        break;
      case 'hearts':
        [1047, 1319, 1568, 2093, 2637].forEach((f, i) =>
          e.tone({ type: 'triangle', freq: f, at: t + i * 0.09, dur: 0.4, gain: 0.06 }),
        );
        break;
      case 'select':
        e.tone({ type: 'square', freq: 1250, dur: 0.03, gain: 0.03 });
        break;
      case 'burn':
        e.hiss({ dur: 0.6, gain: 0.14, filter: 'lowpass', freq: 700 });
        break;
      case 'eggs':
        e.tone({ type: 'square', freq: 900, endFreq: 400, dur: 0.03, gain: 0.08 });
        e.hiss({ at: t + 0.04, dur: 0.3, gain: 0.12, filter: 'highpass', freq: 3200 });
        break;
      case 'bacon':
        e.hiss({ dur: 0.35, gain: 0.14, filter: 'highpass', freq: 3600 });
        break;
      case 'snore':
        e.tone({ type: 'sawtooth', freq: 92, endFreq: 70, dur: 0.8, gain: 0.08, attack: 0.3 });
        e.tone({
          type: 'sawtooth',
          freq: 92,
          endFreq: 70,
          at: t + 1.1,
          dur: 0.8,
          gain: 0.08,
          attack: 0.3,
        });
        break;
    }
  }

  /** Shift-change brawl sounds (reusing the diner's where they fit). */
  playFight(name: FightSfx): void {
    const e = this.engine;
    if (!e.ctx) return;
    const t = e.now;
    switch (name) {
      case 'punch':
        e.tone({ type: 'sine', freq: 170, endFreq: 70, dur: 0.09, gain: 0.4 });
        e.hiss({ dur: 0.07, gain: 0.22, filter: 'bandpass', freq: 1300, q: 1.2 });
        break;
      case 'heavy':
        e.tone({ type: 'sine', freq: 130, endFreq: 42, dur: 0.18, gain: 0.5 });
        e.hiss({ dur: 0.16, gain: 0.32, filter: 'lowpass', freq: 1900 });
        e.tone({ type: 'square', freq: 90, endFreq: 60, dur: 0.1, gain: 0.08 });
        break;
      case 'whiff':
        e.hiss({ dur: 0.13, gain: 0.08, filter: 'bandpass', freq: 1900, endFreq: 650, q: 2 });
        break;
      case 'block':
        e.tone({ type: 'square', freq: 230, endFreq: 180, dur: 0.05, gain: 0.08 });
        e.hiss({ dur: 0.05, gain: 0.12, filter: 'highpass', freq: 3000 });
        break;
      case 'parry':
        e.tone({ type: 'triangle', freq: 1760, dur: 0.3, gain: 0.14 });
        e.tone({ type: 'triangle', freq: 2640, at: t + 0.03, dur: 0.25, gain: 0.08 });
        e.hiss({ dur: 0.08, gain: 0.12, filter: 'highpass', freq: 5000 });
        break;
      case 'toss':
        e.hiss({ dur: 0.24, gain: 0.14, filter: 'bandpass', freq: 600, endFreq: 2400, q: 1.5 });
        break;
      case 'clash':
        e.hiss({ dur: 0.18, gain: 0.3, filter: 'bandpass', freq: 1500, q: 0.8 });
        e.tone({ type: 'square', freq: 900, dur: 0.08, gain: 0.06 });
        e.tone({ type: 'square', freq: 1300, at: t + 0.02, dur: 0.08, gain: 0.05 });
        break;
      case 'super':
        [523, 659, 784, 1047, 1319, 1568].forEach((f, i) =>
          e.tone({ type: 'square', freq: f, at: t + i * 0.045, dur: 0.14, gain: 0.07 }),
        );
        e.tone({ type: 'sawtooth', freq: 110, endFreq: 440, dur: 0.45, gain: 0.08 });
        break;
      case 'calm':
        [1047, 1319, 1568, 2093].forEach((f, i) =>
          e.tone({ type: 'triangle', freq: f, at: t + i * 0.11, dur: 0.5, gain: 0.07 }),
        );
        this.crowd(t + 0.1);
        break;
      case 'lose':
        this.play('walkout');
        break;
      case 'chairCatch':
        this.play('catch');
        this.play('crowdOoh');
        break;
      case 'bell':
        this.play('fightBell');
        break;
      case 'crowd':
        this.play('crowdOoh');
        break;
    }
  }

  /** A synthetic "ooooOOOH!" — a cluster of voices through vowel-ish formant filters. */
  private crowd(t: number): void {
    const e = this.engine;
    const ctx = e.ctx;
    if (!ctx) return;
    const out = ctx.createGain();
    out.gain.setValueAtTime(0.0001, t);
    out.gain.exponentialRampToValueAtTime(0.16, t + 0.25);
    out.gain.exponentialRampToValueAtTime(0.0001, t + 1.3);
    const f1 = ctx.createBiquadFilter();
    f1.type = 'bandpass';
    f1.frequency.value = 450;
    f1.Q.value = 4;
    const f2 = ctx.createBiquadFilter();
    f2.type = 'bandpass';
    f2.frequency.value = 850;
    f2.Q.value = 5;
    f1.connect(out);
    f2.connect(out);
    out.connect(e.sfxBus);
    for (let i = 0; i < 7; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      const base = 140 + Math.random() * 160;
      osc.frequency.setValueAtTime(base, t);
      osc.frequency.linearRampToValueAtTime(base * 1.35, t + 0.45);
      osc.frequency.linearRampToValueAtTime(base * 1.05, t + 1.3);
      osc.connect(f1);
      osc.connect(f2);
      osc.start(t);
      osc.stop(t + 1.4);
    }
  }
}
